type ScrollToHashOptions = {
    timeoutMs?: number;
    behavior?: ScrollBehavior;
};

const getDefaultScrollBehavior = (): ScrollBehavior => {
    if (typeof window === 'undefined') {
        return 'auto';
    }

    const prefersReducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return prefersReducedMotion ? 'auto' : 'smooth';
};

export const scrollToHash = async (hash: string | null | undefined, options: ScrollToHashOptions = {}) => {
    if (typeof document === 'undefined') {
        return;
    }

    if (!hash || hash === '#') {
        return;
    }

    const rawId = hash.startsWith('#') ? hash.slice(1) : hash;
    let id: string;
    try {
        id = decodeURIComponent(rawId);
    } catch {
        id = rawId;
    }

    if (!id) {
        return;
    }

    const timeoutMs = options.timeoutMs ?? 1500;
    const behavior = options.behavior ?? getDefaultScrollBehavior();

    const raf =
        typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 16);

    const start = typeof performance === 'undefined' ? Date.now() : performance.now();

    // Wait a couple frames to let the view mount, then poll until the element exists.
    await new Promise<void>((resolve) => raf(() => raf(() => resolve())));

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior, block: 'start' });
            return;
        }

        const now = typeof performance === 'undefined' ? Date.now() : performance.now();
        if (now - start > timeoutMs) {
            return;
        }

        await new Promise<void>((resolve) => raf(() => resolve()));
    }
};
